/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiBase, LuiInputBase } from '@here/lui-base'
import { LuiSuggestionsMenuMixin } from '@here/lui-base/lui-suggestions-menu-mixin'
import scopedCss from 'lui-typeahead.scss'
import template from 'lui-typeahead.html'

export default class LuiTypeahead extends LuiSuggestionsMenuMixin(LuiInputBase) {
  static get is() {
    return 'lui-typeahead'
  }

  static get attributeToggles() {
    return [
      'disabled',
      'placeholder',
    ]
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  get field() {
    return this._field || this._getField()
  }

  set field(fieldElement) {
    this._field = fieldElement
  }

  // overriden suggestions
  get useSuggestions() {
    return true
  }

  get value() {
    return this.field ? this.field.value : this.getAttribute('value')
  }

  set value(newValue) {
    this.setAttribute('value', newValue)
    this.field.value = newValue
    this.field.setAttribute('value', newValue)
    this.valueChanged('', newValue)
  }

  get uiMenu() {
    if (!this.field) {
      return
    }
    this._uiMenu = this._uiMenu || this.createMenu()
    return this._uiMenu
  }

  // overriden suggestions
  get searchTextInput() {
    this._searchTextInput = this._searchTextInput || this.shadowRoot.querySelector('lui-textfield')
    return this._searchTextInput
  }

  disabledChanged() {
    super.disabledChanged()
    this.toggleCssClass('lui-disabled', this.disabled)

    const _disabledChanged = () => {
      if (this.disabled) {
        this._unregisterEvents()
        this.searchTextInput.setAttribute('disabled', 'disabled')
      } else {
        this._registerEvents()
        this.searchTextInput.removeAttribute('disabled')
      }
    }

    const _isSeedElementAvailable = () => {
      return !!this.seedElement
    }

    this.poll(_disabledChanged, _isSeedElementAvailable)
  }

  init() {
    super.init()

    this._setPlaceholder()
    this._toggleHostClass(this.uiMenu.open)
  }

  onConnected() {
    super.onConnected()

    this.delay(() => {
      this._suggestionsUpdate()
      this._initField()
      this._initMenu()
      this._registerEvents()
      this._checkAutofocus()
    })
  }

  onDisconnected() {
    super.onDisconnected()

    this._unregisterEvents()
  }

  placeholderChanged(oldVal, newVal) {
    this._setPlaceholder(newVal)
  }

  valueChanged(oldVal, newVal) {
    this._valueChanged(oldVal, newVal)
  }

  _addValueListenerToField(field) {
    const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(field)).value

    if (!nativeImplementation) return
    Object.defineProperty(field, 'value', {
      set: (value) => {
        const optionToSet = this._findOptionByValue(value)
        if (optionToSet || value === '') { // TODO: relax when allowCustomInputs is supported
          nativeImplementation.set.call(field, value)
          this._updateTargetInput(optionToSet ? optionToSet.textContent : '')
        }
      },
      get: nativeImplementation.get,
    })
  }

  _checkAutofocus() {
    if (this.getAttribute('autofocus') !== null) {
      this.searchTextInput.field.focus()
      if (document.activeElement === this) {
        this._onFocus()
      }
    }
  }

  _createField() {
    const fieldElement = document.createElement('input')
    fieldElement.type = 'text'
    return fieldElement
  }

  _getField() {
    const field = this.querySelector('input') || this._createField()

    if (!field._wired) {
      this._wireField(field)
    }

    return field
  }

  _initField() {
    this.value = this.value || this.getAttribute('value')
    this.field.tabIndex = -1
  }

  _initMenu() {
    // hack on menu focus
    const uiMenuFocusableElement = this.uiMenu.shadowRoot.querySelector('span[tabindex]')
    if (uiMenuFocusableElement) {
      uiMenuFocusableElement.tabIndex = -1
    }
  }

  _onBlur() {
    this.classList.remove('lui-focus')
  }

  _onFocus() {
    this._suggestionsUpdate()
    this.classList.add('lui-focus')
    this.searchTextInput.field.focus()
  }

  _onMutationOptions(mutations) {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length || mutation.removedNodes.length ||
        mutation.type === 'characterData' || mutation.type === 'attributes') {
        this._suggestionsUpdate()
      }
    })
  }

  _refreshUiTextInput() {
    const option = this._findOptionByValue(this.value)

    if (option) {
      this._updateTargetInput(option.shortLabel)
    } else {
      this._resetFieldAndTextInput()
    }
  }

  _registerEvents() {
    if (this.disabled) {
      return
    }
    this._onBlurBound = this._onBlurBound || this._onBlur.bind(this)
    this._onFocusBound = this._onFocusBound || this._onFocus.bind(this)

    this.addEventListener('blur', this._onBlurBound)
    this.addEventListener('focus', this._onFocusBound)

    this._registerSuggestionsEvents()

    const datalist = this.querySelector('datalist')
    if (datalist) {
      this._optionsObserver = window.lui.createMutationObserver(this._onMutationOptions.bind(this))
      this._optionsObserver.observe(datalist,
        { childList: true, subtree: true, characterDataOldValue: true, attributeOldValue: true })
    }
  }

  _resetFieldAndTextInput() {
    this.value = ''
    this._updateTargetInput('')
  }

  _setPlaceholder(newVal) {
    const placeholder = (newVal || newVal === '') ? newVal : null
    const fieldPlaceholder = (this.field.placeholder || this.field.placeholder === '') ? this.field.placeholder : null

    if (placeholder !== null) {
      this.searchTextInput.setAttribute('placeholder', placeholder)
    } else if (fieldPlaceholder !== null && !this.hasAttribute('placeholder')) {
      this.searchTextInput.setAttribute('placeholder', fieldPlaceholder)
    }
  }

  _unregisterEvents() {
    this.removeEventListener('blur', this._onBlurBound)
    this.removeEventListener('focus', this._onFocusBound)

    this._unregisterSuggestionsEvents()

    if (this._optionsObserver) {
      this._optionsObserver.disconnect()
    }
  }

  // overriden suggestions
  _update() {
    this._refreshOptions()
    this._updateUiMenu(this._getFilteredOptions(this.searchText))
    this._refreshUiTextInput()
  }

  // overriden suggestions
  get suggestionsTargetInput() {
    return this.searchTextInput
  }

  _valueChanged(oldVal, newVal) {
    if (this._initialized) {
      this.field.value = newVal
    }
  }

  _wireField(field) {
    this.constructor.attributeToggles.concat('value').forEach((attributeName) => {
      const attributeValue = this.getAttribute(attributeName)

      if (attributeValue !== null && attributeName !== 'type') {
        if (typeof field[attributeName] !== 'undefined') {
          // Prefer to set in JS if possible; convert values for JS if needed:
          // TODO LUI-1045: Extract React specific behavior (unnecessary check for 'false' value)
          field[attributeName] = LuiBase.isBooleanAttribute(attributeName) ? (attributeValue !== 'false') : attributeValue
        } else {
          // Fallback if attribute has no JS equivalent:
          field.setAttribute(attributeName, attributeValue)
        }
      }
    })
    field.setAttribute('type', 'text')

    if (this._initialized) {
      this._addValueListenerToField(field) // TODO: add other property listeners
      this.seedElement = field
      this.field = field
      this.appendChild(field)
      field._wired = true
    }

    return field
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-typeahead/lui-typeahead.js
// module id = 363
// module chunks = 0 1 20